<template>
    <div id="imageMessage" class="image-message-container" :class="{ received }">
        <div
            class="sc-message--text message-bubble"
            :style="{
                ...messageColors,
                'background-color': messageColors.backgroundColor,
            }"
        >
            <span v-if="isGroupMessage" class="whatsapp-number-first" @click.stop="showWhatsappChatStarterModal = true">
                <a href="javascript:void(0)" :style="{ color: getWhatsappNumberColor() }"
                    >{{ whatsappAuthorName }} - {{ whatsappAuthorNumber }}</a
                >
            </span>
            <div
                v-if="imageSrc()"
                v-tippy="{ followCursor: true, placement: 'bottom-end' }"
                :content="$t('chatbotv2.imageView')"
                class="cb-img-preview"
                @click="show"
                @mouseenter="hovered = true"
                @mouseleave="hovered = false"
            >
                <img
                    :src="imageSrc()"
                    class="img-thumbnail ant-upload-list-picture-card-container"
                    style="object-fit: cover"
                />
                <md-icon v-if="hovered" class="cb-pr-icon"> visibility</md-icon>
            </div>
            <div v-if="getCaption()" class="caption-text">
                <span class="sc-message--text-content">{{ getCaption() }}</span>
            </div>
            <div
                v-if="isGroupMessage"
                style="display: flex; flex-direction: row; justify-content: end; align-items: center; padding: 0 0.3rem"
            >
                <span class="sc-message-meta-new whatsapp-number" @click.stop="showWhatsappChatStarterModal = true">
                    <a href="javascript:void(0)" :style="{ color: getWhatsappNumberColor() }"
                        >{{ whatsappAuthorName }} - {{ whatsappAuthorNumber }}</a
                    >
                </span>
            </div>
            <div class="actor-info-bar" v-if="actorInfo">
                <i class="material-icons">info_outline</i>
                <span v-if="!actorInfo.link">{{ actorInfo.text }}</span>
                <router-link v-else :to="actorInfo.link" class="actor-link">
                    {{ actorInfo.text }}
                    <i class="material-icons" style="font-size: 10px;">open_in_new</i>
                </router-link>
            </div>
        </div>
        <Modal2
            v-if="showWhatsappChatStarterModal"
            @cancel="showWhatsappChatStarterModal = false"
            :title="$t('message.whatsappChatStart')"
            :showFooter="false"
        >
            <WhatsappChatStarter
                :default-phone-number="whatsappAuthorNumber"
                channel-override
                phone-number-override
                @close="showWhatsappChatStarterModal = false"
                :default-name="whatsappAuthorName"
            />
        </Modal2>
    </div>
</template>

<script>
import _ from "lodash";
import { mapState } from "vuex";
import Modal2 from "../../common/Modal2.vue";
import WhatsappChatStarter from "../../../views/Pages/Integrations/meta-leads/WhatsappChatStarter.vue";
import StringUtils from "../../../utils/StringUtils";

export default {
    name: "ImageMessage",
    props: {
        message: {
            type: Object,
            required: true,
        },
        extendedStyle: {
            type: Object,
        },
        messageColors: {
            type: Object,
        },
        received: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            hovered: false,
            previewImg: false,
            showWhatsappChatStarterModal: false,
        };
    },
    computed: {
        ...mapState({
            users: function (state) { return _.get(state, "user.users.data", []); },
        }),
        isGroupMessage() {
            return !!_.get(this.message, "raw.whatsappAuthor.author");
        },
        whatsappAuthorNumber() {
            return _.get(this.message, "raw.whatsappAuthor.author", "").replace(/[^0-9]/g, "");
        },
        whatsappAuthorName() {
            return _.get(this.message, "raw.whatsappAuthor.authorName", "");
        },
        actorInfo: function () {
            const data = this.message.raw || {};
            const actorDetail = data.actorDetail || {};

            if (actorDetail.type === "automation" || data.fromAutomation) {
                const automationId = actorDetail.automation || data.automationId;
                const blockId = actorDetail.blockId;
                const automationName = actorDetail.automationName;
                const text = automationName
                    ? this.$t("message.sentByAutomation", { name: automationName })
                    : this.$t("message.sentByAutomationGeneric");
                let link = null;
                if (automationId) {
                    link = "/automations/" + automationId + "/design" + (blockId ? "/block/" + blockId : "");
                }
                return { text: text, link: link };
            }

            if (actorDetail.type === "chatbot") {
                const storyId = actorDetail.chatbotStory;
                const version = actorDetail.chatbotStoryVersion;
                const blockId = actorDetail.blockId;
                const isAIAssistant = blockId && (blockId === "ai_assistant" || blockId === "AI_ASSISTANT");
                const storyName = actorDetail.chatbotStoryName;
                const text = storyName
                    ? this.$t("message.sentByChatbot", { name: storyName })
                    : this.$t("message.sentByChatbotGeneric");
                let link = null;
                if (storyId && version) {
                    link = "/chatbot/story/" + storyId + "/" + version + (blockId && !isAIAssistant ? "/block/" + blockId : "");
                }
                return { text: text, link: link };
            }

            let text = null;
            if (data.fromBroadcast || actorDetail.type === "broadcast") text = this.$t("message.sentByBroadcast");
            else if (data.fromAPI || actorDetail.type === "api") text = this.$t("message.sentByAPI");
            else {
                let username = data.username || "";
                if (!username && data.user) {
                    const userHashMap = _.keyBy(this.users, "_id");
                    if (typeof data.user === "object") {
                        username = _.get(data.user, "name", "");
                    } else {
                        username = _.get(userHashMap, "[" + data.user + "].name", "");
                    }
                }
                if (username) text = this.$t("message.sentByUser", { name: username });
            }
            return text ? { text: text, link: null } : null;
        },
    },
    methods: {
        show() {
            this.$emit("open-gallery", this.message);
        },
        imageSrc() {
            let asset = "";
            if (!!this.message.isMail) {
                asset = this.message;
            } else {
                asset = this.message.raw.asset;
            }
            const path = asset?.path || "";
            if (path === "") return null;
            const isWhatsapp = path.indexOf("whatsapp") !== -1;
            const isInstagram = path.indexOf("instagram") !== -1;
            const isMessenger = path.indexOf("messenger") !== -1;
            const isTelegram = path.indexOf("telegram") !== -1;
            const isFacebook = path.indexOf("facebook") !== -1;
            const isTable = path.indexOf("tables/attachments") !== -1;
            const isUnipile = path.indexOf("unipile") !== -1;
            const isChat = path.indexOf("chat") !== -1;
            const isApiMessages = path.indexOf("api-messages") !== -1;

            const src =
                isWhatsapp || isInstagram || isTelegram || isMessenger || isFacebook || isTable || isUnipile || isChat || isApiMessages
                    ? `https://static.supsis.live/${path}`
                    : asset.v2Format && asset.cdnUrl
                    ? asset.cdnUrl
                    : `https://static.supsis.live/${path + asset._id + (asset.ext ? "." + asset.ext : "")}`;
            return src;
        },
        getCaption() {
            return _.get(
                this.message,
                "caption",
                _.get(
                    this.message,
                    "raw.caption",
                    _.get(
                        this.message,
                        "raw.message",
                        _.get(this.message, "raw.description", _.get(this.message, "data.text", null))
                    )
                )
            );
        },
        getWhatsappNumberColor() {
            if (this.isGroupMessage) {
                const baseColor = "rgb(241, 244, 247)";
                const numberColor = StringUtils.stringToColor(this.whatsappAuthorNumber);
                return this.getContrastColor(baseColor, numberColor);
            }
            return this.messageColors.color;
        },
        getContrastColor(bgColor, textColor) {
            const colors = ["#1a5fb4", "#813d9c", "#c64600", "#2ec27e", "#e01b24", "#3584e4", "#9141ac", "#f5c211"];
            const index = Math.abs(this.hashCode(this.whatsappAuthorNumber)) % colors.length;
            return colors[index];
        },
        hashCode(str) {
            let hash = 0;
            for (let i = 0; i < str.length; i++) {
                const char = str.charCodeAt(i);
                hash = (hash << 5) - hash + char;
                hash = hash & hash; // Convert to 32bit integer
            }
            return hash;
        },
    },
    components: {
        Modal2,
        WhatsappChatStarter,
    },
};
</script>

<style lang="scss" scoped>
.image-message-container {
    display: flex;
    justify-content: flex-end;
    width: 100%;
    padding: 0 12px;

    &.received {
        justify-content: flex-start !important;
    }
}

.message-bubble {
    display: inline-block;
    padding: 8px;
    border-radius: 6px;
    max-width: 300px;
}

.cb-img-preview {
    max-width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;

    img {
        max-width: 100%;
        border-radius: 4px;
    }

    .cb-pr-icon {
        position: absolute;
        z-index: 2;
    }
}

.caption-text {
    margin-top: 8px;
    font-size: 14px;
    line-height: 1.4;
    word-break: break-word;
}

.cb-img-preview:hover {
    cursor: pointer;
    filter: grayscale(100%);
}

.cb-pr-icon {
    position: absolute;
    pointer-events: none;
}

.whatsapp-number {
    cursor: pointer;

    a {
        text-decoration: underline;
    }
}

.whatsapp-number-first {
    cursor: pointer;
    font-size: smaller;
    font-weight: 200;
    letter-spacing: 2px;

    a {
        &:hover {
            text-decoration: underline;
        }
    }

    padding: 5px;
}

.sc-message-meta-new {
    font-size: 8px;
    font-weight: 300;
    color: white;
    text-align: right;
    border-radius: 6px;
    text-transform: uppercase;
    white-space: nowrap;
    text-overflow: ellipsis;
    line-height: 0px !important;
    margin: 5px !important;
    margin-right: 14px !important;
    margin-bottom: 14px !important;
}

.actor-info-bar {
    display: flex;
    align-items: center;
    gap: 3px;
    padding: 4px 4px 0;
    color: #8696a0;
    font-size: 10px;
    line-height: 1.2;

    .material-icons {
        font-size: 12px;
    }

    .actor-link {
        color: #00a5f4;
        text-decoration: underline;
        cursor: pointer;
        display: flex;
        align-items: center;
        gap: 2px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;

        &:hover {
            opacity: 0.8;
        }
    }
}
</style>
